import React, { useState, useEffect } from 'react';
import DatePicker from './DatePicker';
import './SalesActivity.css';
import LoadingState from './LoadingState';
import ConfirmModal from './ConfirmModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function NPSInput({ user, managedOwner }) {
  const [allEntries, setAllEntries] = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedDealerships, setSelectedDealerships] = useState([]);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');

  // Bulk import states
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStep, setBulkStep] = useState('upload');
  const [bulkEntries, setBulkEntries] = useState([]);
  const [bulkValidation, setBulkValidation] = useState(null);
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkImportError, setBulkImportError] = useState(null);
  const [bulkImportSuccess, setBulkImportSuccess] = useState(null);

  const isOwner = user?.role === 'OWNER' || managedOwner;
  const canBulkImport = user?.role === 'OWNER' || user?.role === 'MANAGER' || user?.role === 'ADMIN' || managedOwner;

  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });

  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toLocalDateString = (dateStr) => {
    if (!dateStr) return '';
    const str = typeof dateStr === 'string' ? dateStr : new Date(dateStr).toISOString();
    return str.split('T')[0];
  };

  useEffect(() => {
    fetchAllData();
  }, [managedOwner]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      let dealershipsEndpoint = isOwner
        ? `${API_URL}/api/owners/dealerships`
        : `${API_URL}/api/dealerships`;

      if (managedOwner && isOwner) {
        dealershipsEndpoint += `?ownerId=${managedOwner.id}`;
      }

      const dealershipsRes = await fetch(dealershipsEndpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (dealershipsRes.ok) {
        const dealershipsData = await dealershipsRes.json();
        const fetchedDealerships = dealershipsData.dealerships || [];
        setDealerships(fetchedDealerships);

        const entriesPromises = fetchedDealerships.map(async (d) => {
          const res = await fetch(
            `${API_URL}/api/dealerships/${d.id}/nps-metrics`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (res.ok) {
            const data = await res.json();
            return (data || []).map(e => ({
              ...e,
              dealershipId: d.id,
              dealershipName: d.name
            }));
          }
          return [];
        });

        const entriesArrays = await Promise.all(entriesPromises);
        setAllEntries(entriesArrays.flat());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewEntry = () => {
    const defaultDealership = dealerships.length > 0 ? dealerships[0].id : '';
    setEditingEntry({
      dealershipId: defaultDealership,
      date: getLocalDateString(),
      nps12Month: '',
      npsMTD: '',
      salesNps12Month: '',
      salesNpsMTD: ''
    });
    setShowForm(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry({
      ...entry,
      date: toLocalDateString(entry.date)
    });
    setShowForm(true);
  };

  const handleDeleteEntry = (id) => {
    setConfirmModal({ isOpen: true, id });
  };

  const confirmDelete = async () => {
    const id = confirmModal.id;
    setConfirmModal({ isOpen: false, id: null });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/nps-metrics/${id}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (!response.ok) throw new Error('Failed to delete entry');
      setAllEntries(prev => prev.filter(entry => entry.id !== id));
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingEntry(null);
  };

  const toggleAllDealerships = () => {
    if (selectedDealerships.length === dealerships.length) {
      setSelectedDealerships([]);
    } else {
      setSelectedDealerships(dealerships.map(d => d.id));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Bulk import functions
  const parseBulkCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV file must contain a header row and at least one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const requiredHeaders = ['dealership', 'date', '12month', 'mtd'];

    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
    }

    const entries = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length !== headers.length) continue;

      const entry = {};
      headers.forEach((header, index) => {
        entry[header] = values[index];
      });
      entries.push(entry);
    }

    return entries;
  };

  const validateBulkEntries = async (entries) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/nps-metrics/bulk-import/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ entries, dealerships })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Validation failed');
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Validation error: ${error.message}`);
    }
  };

  const executeBulkImport = async (entries) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/nps-metrics/bulk-import/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ entries })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Import failed');
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Import error: ${error.message}`);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setBulkFileName(file.name);
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const csvText = event.target.result;
        const parsed = parseBulkCSV(csvText);
        setBulkEntries(parsed);
        setBulkStep('validating');
        setBulkImportError(null);

        const validation = await validateBulkEntries(parsed);
        setBulkValidation(validation);
        setBulkStep('review');
      } catch (error) {
        setBulkImportError(error.message);
        setBulkStep('upload');
      }
    };

    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    try {
      setBulkStep('importing');
      setBulkImportError(null);
      setBulkImportSuccess(null);

      const result = await executeBulkImport(bulkValidation.normalizedEntries);
      setBulkImportSuccess(`Successfully imported ${result.imported} entries${result.skipped > 0 ? `, skipped ${result.skipped}` : ''}`);
      setBulkStep('complete');

      // Refresh data
      await fetchAllData();
    } catch (error) {
      setBulkImportError(error.message);
      setBulkStep('review');
    }
  };

  const activeFilters = selectedDealerships.map(id => {
    const dealership = dealerships.find(d => d.id === id);
    return {
      type: 'dealership',
      value: id,
      label: dealership?.name || id
    };
  });

  const removeFilter = (type, value) => {
    if (type === 'dealership') {
      setSelectedDealerships(selectedDealerships.filter(d => d !== value));
    }
  };

  const filteredEntries = allEntries.filter(entry => {
    if (selectedDealerships.length > 0 && !selectedDealerships.includes(entry.dealershipId)) {
      return false;
    }
    if (searchTerm && !entry.dealershipName.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (sortField === 'date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) return <LoadingState message="Loading NPS data..." fullscreen />;

  return (
    <div className="sales-activity">
      <div className="sales-activity-header">
        <div>
          <h1>Net Promoter Score (NPS)</h1>
          <p>Track customer satisfaction metrics</p>
        </div>
      </div>

      <div className="filter-bar">
        <div className="filter-bar-left">
          <button
            className={`filter-button ${activeFilters.length > 0 ? 'active' : ''}`}
            onClick={() => setShowFilterPanel(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 4h18M3 12h12M3 20h6" strokeLinecap="round" />
            </svg>
            Filters
            {activeFilters.length > 0 && ` (${activeFilters.length})`}
          </button>

          {activeFilters.length > 0 && (
            <div className="active-filters">
              {activeFilters.map((filter, index) => (
                <div key={index} className="active-filter-chip">
                  {filter.label}
                  <button onClick={() => removeFilter(filter.type, filter.value)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {canBulkImport && (
          <div className="bulk-import-container">
            <button
              className="bulk-import-button"
              onClick={() => {
                setShowBulkModal(true);
                setBulkStep('upload');
                setBulkValidation(null);
                setBulkImportError(null);
                setBulkImportSuccess(null);
                setBulkEntries([]);
                setBulkFileName('');
              }}
              title="Bulk Import"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/>
                <path d="M19.4 15a1.6 1.6 0 0 0 .33 1.8l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.8-.33 1.6 1.6 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.09a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.6 1.6 0 0 0 .33-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.09a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.33-1.8l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.6 1.6 0 0 0 1.8.33h.09a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.09a1.6 1.6 0 0 0 1 1.5h.09a1.6 1.6 0 0 0 1.8-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.33 1.8v.09a1.6 1.6 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.09a1.6 1.6 0 0 0-1.51 1Z"/>
              </svg>
            </button>
          </div>
        )}
      </div>

          {/* Filter Panel */}
      {showFilterPanel && (
        <>
          <div className="filter-overlay open" onClick={() => setShowFilterPanel(false)} />
          <div className="filter-panel open">
            <div className="filter-panel-header">
              <h3>Filters</h3>
              <button className="filter-panel-close" onClick={() => setShowFilterPanel(false)}>×</button>
            </div>

            <div className="filter-panel-content">
              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>Dealerships</h4>
                  <button onClick={toggleAllDealerships} className="btn-select-all">
                    {selectedDealerships.length === 0 ? 'Select All' : 'Clear All'}
                  </button>
                </div>
                {dealerships.map(dealership => (
                  <label key={dealership.id} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDealerships.includes(dealership.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDealerships([...selectedDealerships, dealership.id]);
                        } else {
                          setSelectedDealerships(selectedDealerships.filter(d => d !== dealership.id));
                        }
                      }}
                    />
                    <span>{dealership.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {showForm && (
        <div className="activities-form-container">
          <h2 className="form-heading">{editingEntry?.id ? 'Edit NPS Entry' : 'New NPS Entry'}</h2>
          <NPSForm
            entry={editingEntry}
            dealerships={dealerships}
            onClose={handleCloseForm}
            onSave={() => {
              handleCloseForm();
              fetchAllData();
            }}
          />
        </div>
      )}

      <div className="activities-list">
        <div className="activities-controls">
          <div className="search-controls">
            <input
              type="text"
              placeholder="Search dealership..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {!showForm && (
          <button className="btn-new-sale" onClick={handleNewEntry}>
            + New Entry
          </button>
          )}
        </div>

        {sortedEntries.length === 0 ? (
          <div className="no-activities">
            <p>No entries found</p>
            {allEntries.length > 0 && <p className="filter-hint">Try adjusting your filters</p>}
            <button onClick={handleNewEntry}>Add first entry</button>
          </div>
        ) : (
          <div className="activities-table-container">
            <table className="activities-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('dealershipName')} className="sortable">
                    Dealership {sortField === 'dealershipName' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('date')} className="sortable">
                    Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('nps12Month')} className="sortable">
                    Service 12M (%) {sortField === 'nps12Month' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('npsMTD')} className="sortable">
                    Service MTD (%) {sortField === 'npsMTD' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('salesNps12Month')} className="sortable">
                    Sales 12M (%) {sortField === 'salesNps12Month' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('salesNpsMTD')} className="sortable">
                    Sales MTD (%) {sortField === 'salesNpsMTD' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.dealershipName}</td>
                    <td>{toLocalDateString(entry.date)}</td>
                    <td>{entry.nps12Month !== null && entry.nps12Month !== undefined ? `${entry.nps12Month}%` : '-'}</td>
                    <td>{entry.npsMTD !== null && entry.npsMTD !== undefined ? `${entry.npsMTD}%` : '-'}</td>
                    <td>{entry.salesNps12Month !== null && entry.salesNps12Month !== undefined ? `${entry.salesNps12Month}%` : '-'}</td>
                    <td>{entry.salesNpsMTD !== null && entry.salesNpsMTD !== undefined ? `${entry.salesNpsMTD}%` : '-'}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn-edit"
                          onClick={() => handleEditEntry(entry)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDeleteEntry(entry.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {/* Bulk Import Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Bulk Import NPS Entries</h2>
              <button className="modal-close" onClick={() => setShowBulkModal(false)}>×</button>
            </div>

            <div className="modal-body">
              {bulkStep === 'upload' && (
                <div className="bulk-upload-step">
                  <p>Upload a CSV file with the following columns:</p>
                  <ul>
                    <li><strong>dealership</strong> - Dealership name</li>
                    <li><strong>date</strong> - Date (YYYY-MM-DD format)</li>
                    <li><strong>12month</strong> - 12 Month NPS percentage (0-100)</li>
                    <li><strong>mtd</strong> - MTD NPS percentage (0-100)</li>
                  </ul>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="file-input"
                  />
                  {bulkFileName && <p>Selected: {bulkFileName}</p>}
                  {bulkImportError && <div className="error-message">{bulkImportError}</div>}
                </div>
              )}

              {bulkStep === 'validating' && (
                <div className="bulk-validating-step">
                  <p>Validating entries...</p>
                </div>
              )}

              {bulkStep === 'review' && bulkValidation && (
                <div className="bulk-review-step">
                  <p>Found {bulkValidation.valid} valid entries, {bulkValidation.invalid} invalid entries</p>
                  {bulkValidation.errors && bulkValidation.errors.length > 0 && (
                    <div className="validation-errors">
                      <h4>Errors:</h4>
                      <ul>
                        {bulkValidation.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {bulkValidation.valid > 0 && (
                    <button className="btn-primary" onClick={handleBulkImport}>
                      Import {bulkValidation.valid} Entries
                    </button>
                  )}
                </div>
              )}

              {bulkStep === 'importing' && (
                <div className="bulk-importing-step">
                  <p>Importing entries...</p>
                </div>
              )}

              {bulkStep === 'complete' && (
                <div className="bulk-complete-step">
                  {bulkImportSuccess && <div className="success-message">{bulkImportSuccess}</div>}
                  <button className="btn-primary" onClick={() => setShowBulkModal(false)}>
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, id: null })}
        onConfirm={confirmDelete}
        title="Delete Entry"
        message="Are you sure you want to delete this NPS entry? This cannot be undone."
        confirmStyle="danger"
      />
    </div>
  );
}

function NPSForm({ entry, dealerships, onClose, onSave }) {
  const [formData, setFormData] = useState({
    date: entry?.date ? entry.date : new Date().toISOString().split('T')[0],
    dealershipId: entry?.dealershipId || '',
    nps12Month: entry?.nps12Month !== null && entry?.nps12Month !== undefined ? entry.nps12Month : '',
    npsMTD: entry?.npsMTD !== null && entry?.npsMTD !== undefined ? entry.npsMTD : '',
    salesNps12Month: entry?.salesNps12Month !== null && entry?.salesNps12Month !== undefined ? entry.salesNps12Month : '',
    salesNpsMTD: entry?.salesNpsMTD !== null && entry?.salesNpsMTD !== undefined ? entry.salesNpsMTD : ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nps12Month && !formData.npsMTD && !formData.salesNps12Month && !formData.salesNpsMTD) {
      setMessage({ type: 'error', text: 'Please provide at least one NPS value' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const payload = {
        date: formData.date,
        nps12Month: formData.nps12Month !== '' ? parseFloat(formData.nps12Month) : null,
        npsMTD: formData.npsMTD !== '' ? parseFloat(formData.npsMTD) : null,
        salesNps12Month: formData.salesNps12Month !== '' ? parseFloat(formData.salesNps12Month) : null,
        salesNpsMTD: formData.salesNpsMTD !== '' ? parseFloat(formData.salesNpsMTD) : null
      };

      const url = entry?.id
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/nps-metrics/${entry.id}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/dealerships/${formData.dealershipId}/nps-metrics`;

      const method = entry?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save entry');
      }

      await onSave();
      onClose();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="sales-activity-form">
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="date">Date *</label>
          <DatePicker
            asString
            hideLabel
            className="dp-form"
            selectedDate={formData.date}
            onDateChange={(val) => handleChange({ target: { name: 'date', value: val } })}
          />
        </div>

        {!entry?.id && (
          <div className="form-group">
            <label htmlFor="dealershipId">Dealership *</label>
            <select
              id="dealershipId"
              name="dealershipId"
              value={formData.dealershipId}
              onChange={handleChange}
              required
            >
              <option value="">Select Dealership</option>
              {dealerships.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label style={{ fontWeight: 700, color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Service NPS</label>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="nps12Month">Service 12 Month (%)</label>
          <input
            type="number"
            id="nps12Month"
            name="nps12Month"
            value={formData.nps12Month}
            onChange={handleChange}
            min="0"
            max="100"
            step="0.1"
            placeholder="0-100"
          />
        </div>
        <div className="form-group">
          <label htmlFor="npsMTD">Service MTD (%)</label>
          <input
            type="number"
            id="npsMTD"
            name="npsMTD"
            value={formData.npsMTD}
            onChange={handleChange}
            min="0"
            max="100"
            step="0.1"
            placeholder="0-100"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label style={{ fontWeight: 700, color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sales NPS</label>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="salesNps12Month">Sales 12 Month (%)</label>
          <input
            type="number"
            id="salesNps12Month"
            name="salesNps12Month"
            value={formData.salesNps12Month}
            onChange={handleChange}
            min="0"
            max="100"
            step="0.1"
            placeholder="0-100"
          />
        </div>
        <div className="form-group">
          <label htmlFor="salesNpsMTD">Sales MTD (%)</label>
          <input
            type="number"
            id="salesNpsMTD"
            name="salesNpsMTD"
            value={formData.salesNpsMTD}
            onChange={handleChange}
            min="0"
            max="100"
            step="0.1"
            placeholder="0-100"
          />
        </div>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onClose} className="btn-cancel" disabled={loading}>
          Cancel
        </button>
        <button type="submit" className="btn-submit" disabled={loading}>
          {loading ? 'Saving...' : (entry?.id ? 'Update Entry' : 'Add Entry')}
        </button>
      </div>
    </form>
  );
}
